/**
 * Pure (React-free) markdown parser for the project CHANGELOG.md.
 *
 * Supports the subset the changelog actually uses: H1-H6, paragraphs with
 * soft-wrapping, **bold**, *italic*, `inline code`, [links](url), bullet and
 * numbered lists (nested, with code blocks inside items), fenced code blocks,
 * pipe tables and > blockquotes.
 *
 * It lives apart from ChangelogPage.tsx so it can be unit-tested against the
 * real CHANGELOG.md (src/utils/changelogParser.test.ts) without pulling in
 * Ant Design. If the changelog outgrows this, swap in `react-markdown`.
 */

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: Inline[] }
  | { type: 'italic'; children: Inline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: Inline[] };

export interface ListItem {
  /** Item content: first a paragraph, then any nested lists / code blocks. */
  blocks: Block[];
}

export type Block =
  | { type: 'heading'; level: number; inline: Inline[] }
  | { type: 'paragraph'; inline: Inline[] }
  | { type: 'list'; ordered: boolean; items: ListItem[] }
  | { type: 'code'; lang: string; text: string }
  | { type: 'table'; header: Inline[][]; rows: Inline[][][] }
  | { type: 'quote'; blocks: Block[] };

export interface VersionHeading {
  /** "Unreleased" or "x.y.z" */
  version: string;
  isUnreleased: boolean;
  /** False when the heading is not "[version] — date" (shown as raw text). */
  matched: boolean;
  /** Text after the dash, e.g. "2026-08-05" ("" when absent). */
  date: string;
  raw: string;
}

export interface ChangelogSection {
  heading: VersionHeading;
  body: string;
}

// ── Inline ─────────────────────────────────────────────────────────────────

// Order matters: code first (double-backtick before single) so `/brain/*` globs
// are never read as emphasis.
const INLINE_TOKEN_SOURCE = String.raw`(\`\`.+?\`\`|\`[^\`]+\`|\*\*.+?\*\*|\[[^\]]+\]\([^)\s]+\)|(?<![*\w])\*[^*\s](?:[^*]*[^*\s])?\*(?![*\w]))`;
// `\/(?!\/)` excludes protocol-relative //host URLs (cross-origin).
const SAFE_HREF = /^(https?:\/\/|\/(?!\/)|#|mailto:)/i;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  // Fresh regex per call: a shared /g regex would have its lastIndex clobbered
  // by the recursive calls below (infinite loop).
  const re = new RegExp(INLINE_TOKEN_SOURCE, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', text: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith('``')) {
      // CommonMark double-backtick span (lets the content hold a backtick);
      // one padding space on each side is stripped.
      out.push({ type: 'code', text: tok.slice(2, -2).replace(/^ (.*) $/, '$1') });
    } else if (tok.startsWith('`')) {
      out.push({ type: 'code', text: tok.slice(1, -1) });
    } else if (tok.startsWith('**')) {
      out.push({ type: 'bold', children: parseInline(tok.slice(2, -2)) });
    } else if (tok.startsWith('[')) {
      const close = tok.indexOf('](');
      const label = tok.slice(1, close);
      const href = tok.slice(close + 2, -1);
      if (SAFE_HREF.test(href)) out.push({ type: 'link', href, children: parseInline(label) });
      else out.push({ type: 'text', text: tok });
    } else {
      out.push({ type: 'italic', children: parseInline(tok.slice(1, -1)) });
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push({ type: 'text', text: text.slice(last) });
  return out;
}

/** Plain-text projection of inline nodes (used by tests and fallbacks). */
export function inlineToText(nodes: Inline[]): string {
  return nodes
    .map((n) => (n.type === 'text' || n.type === 'code' ? n.text : inlineToText(n.children)))
    .join('');
}

// ── Blocks ─────────────────────────────────────────────────────────────────

const FENCE = /^\s*```(\S*)\s*$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const LIST_MARKER = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const QUOTE = /^\s*>\s?(.*)$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

function indentOf(line: string): number {
  return line.length - line.trimStart().length;
}

function splitRow(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/) // `\|` is a literal pipe inside a cell
    .map((c) => c.trim().replace(/\\\|/g, '|'));
}

function isBlockStart(line: string, next: string | undefined): boolean {
  const t = line.trim();
  return (
    FENCE.test(line) ||
    HEADING.test(t) ||
    LIST_MARKER.test(line) ||
    QUOTE.test(line) ||
    (t.startsWith('|') && next !== undefined && TABLE_SEPARATOR.test(next))
  );
}

export function parseBlocks(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      i++;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const fenceIndent = indentOf(line);
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE.test(lines[i])) {
        // Remove the fence's own indentation, keep any deeper code indentation.
        body.push(lines[i].slice(Math.min(fenceIndent, indentOf(lines[i]))));
        i++;
      }
      i++; // closing fence (or EOF for an unclosed one; the lint reports it)
      blocks.push({ type: 'code', lang: fence[1], text: body.join('\n') });
      continue;
    }

    const heading = HEADING.exec(trimmed);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, inline: parseInline(heading[2].trim()) });
      i++;
      continue;
    }

    if (trimmed.startsWith('|') && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1])) {
      const header = splitRow(trimmed).map(parseInline);
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        inner.push(QUOTE.exec(lines[i])![1]);
        i++;
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(inner) });
      continue;
    }

    const marker = LIST_MARKER.exec(line);
    if (marker) {
      const baseIndent = marker[1].length;
      const ordered = /\d/.test(marker[2]);
      const items: ListItem[] = [];

      while (i < lines.length) {
        const m = LIST_MARKER.exec(lines[i]);
        if (!m || m[1].length !== baseIndent) break;

        // Item = marker line + following lines that are indented deeper, or
        // lazy soft-wrap continuations, or blank lines followed by deeper lines.
        // Width of the marker plus its following spaces ("- ", "1. ", "10.  ").
        const contentIndent = m[1].length + (m[0].length - m[1].length - m[3].length);
        const itemLines: string[] = [m[3]];
        i++;
        while (i < lines.length) {
          const l = lines[i];
          if (l.trim() === '') {
            let j = i;
            while (j < lines.length && lines[j].trim() === '') j++;
            if (j < lines.length && indentOf(lines[j]) > baseIndent) {
              itemLines.push('');
              i++;
              continue;
            }
            break;
          }
          if (indentOf(l) > baseIndent) {
            itemLines.push(l.slice(Math.min(indentOf(l), contentIndent)));
            i++;
            continue;
          }
          // Not indented deeper: lazy continuation only if it is plain text
          // directly after a non-blank line (a sibling marker/heading ends the item).
          if (!isBlockStart(l, lines[i + 1]) && itemLines[itemLines.length - 1].trim() !== '') {
            itemLines.push(l.trim());
            i++;
            continue;
          }
          break;
        }
        items.push({ blocks: parseBlocks(itemLines) });
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    // Paragraph: soft-wrapped lines until a blank line or another block starts.
    const para: string[] = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !isBlockStart(lines[i], lines[i + 1])) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: 'paragraph', inline: parseInline(para.join(' ')) });
  }

  return blocks;
}

export function parseMarkdown(md: string): Block[] {
  return parseBlocks(md.replace(/\r\n?/g, '\n').split('\n'));
}

// ── Sections ───────────────────────────────────────────────────────────────

export function parseVersionHeading(raw: string): VersionHeading {
  const m = /^\[([^\]]+)\]\s*(?:[—–-]\s*(.+))?$/.exec(raw.trim());
  if (!m) return { version: raw.trim(), isUnreleased: false, matched: false, date: '', raw };
  return {
    version: m[1],
    isUnreleased: m[1].toLowerCase() === 'unreleased',
    matched: true,
    date: m[2] ?? '',
    raw,
  };
}

/**
 * Splits the changelog into an intro (title + text before the first version)
 * and one section per `## [version] — date` heading. `## ` lines inside fenced
 * code blocks are NOT treated as headings.
 */
export function splitChangelog(md: string): { intro: string; sections: ChangelogSection[] } {
  const intro: string[] = [];
  const groups: { heading: string; lines: string[] }[] = [];
  let inFence = false;
  const rawLines = md.replace(/\r\n?/g, '\n').split('\n');
  // Fail open: with an odd number of fences (unterminated block) a plain toggle
  // would swallow every later release into one section, so ignore fences then.
  const trackFences = rawLines.filter((l) => /^\s*```/.test(l)).length % 2 === 0;

  for (const raw of rawLines) {
    if (trackFences && /^\s*```/.test(raw)) inFence = !inFence;
    if (!inFence && /^## /.test(raw)) {
      groups.push({ heading: raw.slice(3).trim(), lines: [] });
      continue;
    }
    (groups.length ? groups[groups.length - 1].lines : intro).push(raw);
  }

  return {
    intro: intro.join('\n'),
    sections: groups.map((g) => ({ heading: parseVersionHeading(g.heading), body: g.lines.join('\n') })),
  };
}

/** True when `text` looks like the changelog and not e.g. an SPA index.html. */
export function looksLikeChangelog(text: string): boolean {
  const head = text.trimStart().slice(0, 200).toLowerCase();
  if (head.startsWith('<!doctype') || head.startsWith('<html')) return false;
  return /^## \[/m.test(text);
}
